import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Button, Input } from '@/components/common';
import { relayPayUrlSchema } from '@/utils/validation';
import { useSetMerchantUrl } from '@/api';
import { useSetMetaForUi } from '@/api/queryHooks/useHostedCommerceUiController';
import styles from './Steps.module.css';

interface ChooseUrlProps {
  finish: () => void;
}

interface FormData {
  url: string;
}

export const ChooseUrl = ({ finish }: ChooseUrlProps) => {
  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(relayPayUrlSchema),
    defaultValues: { url: '' },
    mode: 'onTouched',
  });
  const { mutateAsync } = useSetMetaForUi();

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      await mutateAsync({
        preferredHostedUrl: data?.url,
      });
      finish();
    } catch (e) {
      toast.error('Error. Try again later', {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <h3 className={styles.title}>{'Please let us know your preferred\nURL. *'}</h3>
      <p className={styles.description}>
        All URLs will end with relaypay.io. An example of a URL could be &quot;yourcompany&quot;.relaypay.io
      </p>
      <Controller
        name="url"
        control={control}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            className={styles.input}
            label="URL"
            placeholder="yourcompany.relaypay.io"
            errorMessage={fieldState.error?.message}
            maxLength={63}
          />
        )}
      />
      <Button fluid type="submit" isDisabled={!isDirty || !isValid}>
        Submit
      </Button>
    </form>
  );
};
